import React from "react"
import { Navigate, useLocation } from "react-router-dom"
import { useUser } from "util/UserContext"
import LoadingSkeleton from "ui/LoadingSkeleton"
import { hasExistingAccountCookie } from "util/existingUserCookie"
import {
    buildSigninUrl,
    sanitizeDestination,
} from "util/authDestination"
import {
    getAuthGateUiStrings,
    resolveAuthGateLocale,
} from "i18n/authGateUi"

type LoadingVariant = React.ComponentProps<typeof LoadingSkeleton>["variant"]

interface RequireAuthProps {
    /** Children rendered only when an authenticated user is present. */
    children: React.ReactNode
    /**
     * Loading skeleton variant rendered during the brief `awaitingUser`
     * window (before we know whether the viewer is signed in). Callers
     * can pass a variant that visually matches the guarded surface
     * (e.g. `"entity-avatar"` for chat, `"settings"` for settings) so
     * the skeleton does not feel generic.
     *
     * Ignored when `loadingFallback` is provided.
     */
    loadingVariant?: LoadingVariant
    /**
     * Optional class applied to the loading container. Defaults to a
     * centered layout so the skeleton reads as a page-level surface.
     *
     * Ignored when `loadingFallback` is provided — the custom node is
     * responsible for its own layout.
     */
    className?: string
    /**
     * Optional fully custom loading node rendered during the
     * `awaitingUser` window instead of the default skeleton. Callers
     * that already know the guarded surface's chrome (e.g. chat) pass
     * a static shell so the page layout appears immediately and only
     * the data-bound region reads as a skeleton, avoiding the
     * "lonely single skeleton" flash.
     */
    loadingFallback?: React.ReactNode
}

/**
 * Guards a route (or subtree) that must only render when the viewer
 * is authenticated — Reddit-style redirect flow.
 *
 * Behavior:
 * - While `awaitingUser` (auth is being hydrated on app boot), renders
 *   a localized `LoadingSkeleton` so the redirect does not flash for
 *   viewers who are already signed in.
 * - When the viewer is signed out, redirects to
 *   `/signin?dest=<encoded-url>` for returning visitors (those with
 *   the `existing_account` cookie) or `/signup?dest=<encoded-url>`
 *   for first-time visitors. The redirect uses `<Navigate replace />`
 *   so the guarded URL is not added to the history stack.
 * - When the viewer is authenticated, renders `children`.
 *
 * The destination query param is sanitized via `buildSigninUrl` at
 * the redirect site, and again at the read sites
 * (`AuthLogin`, `usePostAuthRedirect`). After successful auth, the
 * top-level `usePostAuthRedirect` hook (mounted in `AppShell`)
 * consumes the stored destination and routes the viewer back to the
 * original URL — including across OAuth popup / redirect
 * round-trips. See ADR 015.
 */
const RequireAuth: React.FC<RequireAuthProps> = ({
    children,
    loadingVariant = "default",
    className = "max-w-xl mx-auto px-4 py-16",
    loadingFallback,
}) => {
    const { currentUser, awaitingUser } = useUser()
    const location = useLocation()

    const strings = getAuthGateUiStrings(resolveAuthGateLocale())

    if (awaitingUser) {
        // Custom loading surfaces (e.g. the chat inbox shell) render their
        // own chrome and skeletons, so we hand rendering off entirely rather
        // than wrapping them in the default centered skeleton container.
        if (loadingFallback) {
            return <>{loadingFallback}</>
        }

        return (
            <div
                className={`require-auth-loading ${className}`}
                role="status"
                aria-live="polite"
                aria-busy="true"
                aria-label={strings.loadingAriaLabel}
            >
                <LoadingSkeleton
                    variant={loadingVariant}
                    ariaLabel={strings.loadingAriaLabel}
                    count={1}
                />
            </div>
        )
    }

    if (!currentUser) {
        // Compose the current URL as the post-auth destination. When
        // the current path is itself unsafe (hostile, external, or an
        // auth-chrome route), the sanitizer returns `null` and
        // `buildSigninUrl` yields the bare `/signin` or `/signup`
        // path — which is the correct behavior: nothing legitimate
        // should be returned to.
        const currentDest = sanitizeDestination(
            `${location.pathname}${location.search}${location.hash}`
        )
        const mode = hasExistingAccountCookie() ? "signin" : "signup"
        const redirectTo = buildSigninUrl(currentDest, mode)
        return <Navigate to={redirectTo} replace />
    }

    return <>{children}</>
}

export default RequireAuth
